import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { sortByField } from '../../utils/sort.utils';

export interface DropdownItem {
  label: string;
  value: number;
  showBranchNumber: boolean;
  hasSections: boolean;
  sections: any[];
  defaultRegion: any;
  hasSubsidiaries: boolean;
  subsidiaries: any[];
  batchImportKey?: string;
}

function mapToDropDownItem(el: any): DropdownItem {
  return {
    label: el.pseudonym || el.name,
    value: el.id,
    showBranchNumber: el.show_branch_number,
    hasSections: !!el.sections.length,
    sections: el.sections,
    defaultRegion: el.default_region,
    hasSubsidiaries: !!el.subsidiaries.length,
    subsidiaries: el.subsidiaries,
    batchImportKey: el.batch_import_key
  };
}

@Injectable({
  providedIn: 'root',
})
export class CompanySelectorService {
  private _companyId$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private _sectionId$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private _selectedCompanyIndex$: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private _selectedCompany$: BehaviorSubject<DropdownItem | null> = new BehaviorSubject<DropdownItem | null>(null);
  private _companies$: BehaviorSubject<DropdownItem[]> = new BehaviorSubject<DropdownItem[]>([]);

  companyId$ = this._companyId$.asObservable();
  sectionId$ = this._sectionId$.asObservable();
  selectedCompanyIndex$ = this._selectedCompanyIndex$.asObservable();
  selectedCompany$ = this._selectedCompany$.asObservable();
  companies$ = this._companies$.asObservable();

  constructor(private cookieService: CookieService) {}

  setCompanyId(companyId: number, sectionId: number | null = null) {
    this.cookieService.set('company_id', companyId.toString(), 0, '/');

    // Section-ID speichern oder löschen
    if (sectionId !== null) {
      this.cookieService.set('section_id', sectionId.toString(), 0, '/');
      this._sectionId$.next(sectionId);
    } else {
      this.cookieService.delete('section_id', '/');
      this._sectionId$.next(null);
    }

    // setSelectedData first, because it sets cookies data, which is relevant in companyId$ subscriptions
    this.setSelectedData(companyId);

    this._companyId$.next(companyId);
  }

  getCompanyId() {
    return this._companyId$.getValue();
  }

  getSectionId() {
    return this._sectionId$.getValue();
  }

  getSelectedCompany(): DropdownItem | undefined {
    return this._companies$.getValue().find((el: DropdownItem) => el.value === this.getCompanyId());
  }

  setCompanies(items: any[]) {
    if (!items || items.length === 0) {
      console.log('CompanySelectorService: Keine Companies erhalten');
      this._companies$.next([]);
      return;
    }

    const sortedItems = items.map(mapToDropDownItem).sort(sortByField('label'));

    if (sortedItems.length === 0) {
      console.log('CompanySelectorService: Keine gültigen Companies nach Mapping');
      this._companies$.next([]);
      return;
    }

    // set companies data
    this._companies$.next(sortedItems);

    // get selected companyId and sectionId from cookies or first as default
    const cookieCompanyId = this.cookieService.get('company_id');
    const cookieSectionId = this.cookieService.get('section_id');

    const companyId = cookieCompanyId && !isNaN(parseInt(cookieCompanyId))
      ? parseInt(cookieCompanyId)
      : sortedItems[0].value;

    const sectionId = cookieSectionId && !isNaN(parseInt(cookieSectionId))
      ? parseInt(cookieSectionId)
      : null;

    this.setCompanyId(companyId, sectionId);
  }

  private setSelectedData(companyId: number) {
    const companies = this._companies$.getValue();

    // on page load the list of companies if empty
    if (!companies?.length) {
      return;
    }

    let target = companies.find((el: DropdownItem) => el.value === companyId);

    // Fallback wenn Company nicht gefunden wurde
    if (!target) {
      console.warn(`CompanySelectorService: Company mit ID ${companyId} nicht gefunden, verwende erste verfügbare`);
      target = companies[0];
    }

    // Rest der Methode bleibt gleich...
    this.cookieService.set('showBranchNumber', target.showBranchNumber ? '1' : '0', 0, '/');
    this.cookieService.set('hasSections', target.hasSections ? '1' : '0', 0, '/');
    this.cookieService.set('hasSubsidiaries', target.hasSubsidiaries ? '1' : '0', 0, '/');

    let subsidiaries;
    if(target.hasSubsidiaries) {
      subsidiaries = Object.entries(target.subsidiaries).map(([_, value]) => {
        return {
          value: value.id,
          label: value.pseudonym || value.name,
        };
      });
      this.cookieService.set('subsidiaries', JSON.stringify(subsidiaries), 0, '/');
    }

    let defaultRegion;
    if(target.hasSections) {
      defaultRegion = Object.entries(target.sections).map(([_, value]) => {
        return {
          defaultRegion: value.default_region,
          section: value.id,
        };
      });
    } else {
      defaultRegion = target.defaultRegion;
    }
    this.cookieService.set('defaultRegion', JSON.stringify(defaultRegion), 0, '/');
    this._selectedCompany$.next(target);

    let targetIndex = companies.findIndex((el: DropdownItem) => el.value === companyId);
    if(targetIndex === -1 && companies.length > 0) {
      targetIndex = 0;
    }

    this._selectedCompanyIndex$.next(targetIndex);
  }
}
